'use client';

import { IS_EVIDENCE_LIBRARY_ENABLED } from '@/components/constants/flags';
import { useIsStandaloneCalculatorsEnabled } from '@/features/flags';
import { useIsCpdSidebarSkeletonShown } from '@/hooks/useIsCpdSurfaceEnabled';
import { Skeleton } from '@heidi/design-system/skeleton';
import { useMemo } from 'react';

/**
 * Skeleton loader for the evidence/chat sidebar.
 * Matches the layout of ChatSidebar in embedded mode:
 * search input, Library/Calculators/CPD links, and chat list.
 */
export function EvidenceSidebarSkeleton() {
  const enableLibrary = IS_EVIDENCE_LIBRARY_ENABLED();
  const enableCalculators = useIsStandaloneCalculatorsEnabled();
  const showCPD = useIsCpdSidebarSkeletonShown();

  const chatWidths = useMemo(
    () => Array.from({ length: 8 }).map(() => Math.floor(Math.random() * 40) + 40),
    []
  );

  return (
    <div className="flex h-full flex-col gap-2 overflow-hidden" aria-hidden="true">
      {/* Search input - matches ChatSidebar h-8 */}
      <div>
        <Skeleton className="h-8 w-full rounded-md" />
      </div>

      {/* Links Section - Library, Calculators, and CPD */}
      <div className="flex flex-col gap-1">
        {enableLibrary && <Skeleton className="h-8 w-20 rounded-md" />}
        {enableCalculators && <Skeleton className="h-8 w-24 rounded-md" />}
        {showCPD && <Skeleton className="h-8 w-14 rounded-md" />}
      </div>

      {/* Chat list - ghost-style items */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="flex flex-col gap-2">
          {/* "Recent" section header */}
          <Skeleton className="h-3 w-12" />
          <div className="flex flex-col gap-0.5">
            {chatWidths.map((width, i) => (
              <Skeleton
                key={i}
                className="h-8 rounded-md"
                style={{ width: `${width}%`, opacity: 1 - i * 0.08 }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* CPD Activity Card placeholder */}
      {showCPD && <Skeleton className="h-12 w-full rounded-lg" />}
    </div>
  );
}
